import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { rejectionTypeToPolicyTypeMap } from '@ourbranch/lookups';

import { getErrorData } from 'common/constants/get-error-data';
import { ConfirmOverrideModal } from 'common/components/confirm-override-modal';
import useSession from 'core/hooks/use-session';
import { Label } from 'core';
import { useStore } from 'core/store';
import { Tooltip } from 'core/components/tooltip';
import { Button } from 'core/components/button';
import { generateGoogleMapsLink, generateZillowLink } from 'core/helpers/property-view-links';
import Rejection from 'core/assets/svg/ic-rejection.svg';
import Solution from 'core/assets/svg/ic-solution.svg';
import StepsToResolve from 'core/assets/svg/ic-steps-to-resolve.svg';
import Caret from 'core/assets/svg/caretOrange.svg';
import useStyles from './rejections.styles';

const typeToName = {
  home: 'Home',
  auto: 'Auto',
  renters: 'Renters',
  condo: 'Condo',
  monoline: 'Monoline auto',
  'monoline-home': 'Monoline home',
  'monoline-condo': 'Monoline condo'
};

export const Rejections = () => {
  const { isAgency, isInternalAgent, isInternalSales, canClearUWFormRejections } = useSession();
  const {
    offer: store,
    quote: { rejections }
  } = useStore();
  const { values } = useFormikContext();
  const classes = useStyles();
  const [openRejections, setOpenRejections] = useState(rejections.length > 0 ? [rejections[0].code] : []);
  const [confirmModalData, setConfirmModalData] = useState(null);

  const toggleRejectionOpen = (code) => {
    if (openRejections.includes(code)) {
      setOpenRejections(openRejections.filter((openCode) => openCode !== code));
    } else {
      setOpenRejections([...openRejections, code]);
    }
  };

  const location = {
    address: values.address,
    address2: values.address2,
    city: values.city,
    state: values.state,
    zip: values.zip
  };

  return (
    <div className={classes.container}>
      {confirmModalData !== null && (
        <ConfirmOverrideModal
          error={confirmModalData}
          onClose={() => {
            setConfirmModalData(null);
          }}
          canOverrideWithoutSaving
        />
      )}

      {rejections.map((error) => {
        // don't show rejections that aren't related to requested policy types
        if (!rejectionTypeToPolicyTypeMap[error.type]?.includes(values.policyType)) {
          return null;
        }

        // do not show soft rejections on the quote page
        if (error.level === 'soft') {
          return null;
        }

        const rejectionData = getErrorData({
          error,
          isAgency,
          state: store.state,
          errorType: 'REJECTION',
          googleLink: generateGoogleMapsLink(location),
          zillowLink: generateZillowLink(location),
          canClearUWFormRejections
        });

        const isOpen = openRejections.includes(rejectionData.code);

        return (
          <div className={classes.rejection} key={`rejection-${rejectionData.code}`}>
            <div className={classes.inside}>
              {/* top row */}
              <div className={classes.topRow}>
                <div className={classes.row}>
                  <img src={Rejection} alt="rejection" />
                  <div className={classes.titleColumn}>
                    <div className={classes.code}>{`rejection: ${rejectionData.code}`.toUpperCase()}</div>
                    <div className={classes.title}>
                      {typeToName[rejectionData.type]} ineligible: {rejectionData.title}
                    </div>
                  </div>
                </div>
                <div className={classes.row}>
                  {isInternalAgent && isInternalSales && rejectionData?.branchPlusEligible && (
                    <div className={classes.branchPlusEligible}>
                      Branch Plus eligible
                      <Tooltip
                        text="Although we may not be able to write this coverage due to this reason, our partnered providers typically do."
                        placement="top"
                      />
                    </div>
                  )}
                  <Button className={classes.button} onClick={() => toggleRejectionOpen(rejectionData.code)}>
                    <img src={Caret} alt="open/close" className={isOpen ? classes.rotateIcon : null} />
                  </Button>
                </div>
              </div>
              {isOpen && (
                <>
                  {/* subtitle */}
                  {rejectionData.subtitle && <div className={classes.subtitle}>{rejectionData.subtitle}</div>}

                  {/* why box */}
                  {rejectionData.why && (
                    <div className={classes.subBox}>
                      <div className={classes.iconRow}>
                        <img src={Solution} alt="why?" className={classes.icon} /> WHY?
                      </div>
                      <div className={classes.boxContent}>{rejectionData.why}</div>
                    </div>
                  )}

                  {/* steps to resolve box */}
                  {rejectionData.nextSteps && (
                    <div className={classes.subBox}>
                      <div className={classes.iconRow}>
                        <img src={StepsToResolve} alt="Steps To Resolve" className={classes.icon} /> STEPS TO RESOLVE
                      </div>
                      <div className={classes.boxContent}>{rejectionData.nextSteps}</div>
                    </div>
                  )}
                  {rejectionData.canBeOverriddenWithPermissionGroup && canClearUWFormRejections && (
                    <Button
                      className={classes.overrideButton}
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        setConfirmModalData(rejectionData);
                      }}
                    >
                      <Label>Override error</Label>
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Rejections;
